import React, { useState, useEffect } from "react";
import { trackError } from "../../../../lib/metrics";

const EMPLOYEES_PUZZLE = [
  { id: 101, prenom: "Alexandre", nom: "Morel", poste: "Responsable produit", equipe: "Origami Core" },
  { id: 118, prenom: "Samir", nom: "Haddad", poste: "Ingénieur fiabilité", equipe: "Infra & SRE" },
  { id: 132, prenom: "Clara", nom: "Nguyen", poste: "Data scientist", equipe: "Origami ML" },
  { id: 147, prenom: "Julien", nom: "Perez", poste: "Développeur backend", equipe: "Plateforme" },
  { id: 173, prenom: "Yassine", nom: "Benkacem", poste: "Analyste sécurité", equipe: "SecOps" },
  { id: 181, prenom: "Zoé", nom: "Leroy", poste: "Chargée de communication", equipe: "Com externe" },
  { id: 194, prenom: "Martin", nom: "Schmitt", poste: "Tech lead frontend", equipe: "Origami UI" },
  { id: 221, prenom: "Hugo", nom: "Bernard", poste: "Stagiaire QA", equipe: "Qualité" },
  { id: 248, prenom: "Thomas", nom: "Dubois", poste: "Admin systèmes", equipe: "Infra" },
  { id: 272, prenom: "Olivier", nom: "Garnier", poste: "Directeur technique", equipe: "Direction" },
  { id: 286, prenom: "Nadia", nom: "El Mansouri", poste: "Data engineer", equipe: "Origami Data" },
  { id: 297, prenom: "Lucas", nom: "Fournier", poste: "Chef de projet", equipe: "Delivery" },
  { id: 312, prenom: "Amira", nom: "Belkacem", poste: "Juriste", equipe: "Legal" },
  { id: 328, prenom: "Victor", nom: "Laurent", poste: "Ingénieur réseau", equipe: "Infra" },
  { id: 341, prenom: "Sofia", nom: "Martins", poste: "Scrum master", equipe: "Origami Core" },
  { id: 359, prenom: "Marie", nom: "Dupont", poste: "Analyste linguistique", equipe: "Origami NLP" },
  { id: 374, prenom: "Antoine", nom: "Leclerc", poste: "Responsable qualité", equipe: "Qualité" },
  { id: 389, prenom: "Camille", nom: "Henry", poste: "Chef de produit", equipe: "PaperFold Watch" },
];

const CORRECT_ANSWER = 272;
const STORAGE_KEY = "s2p5_eliminated_employees";
const SUCCESS_KEY = "s2p5_puzzle_solved";

const COLORS = [
  "from-blue-500 to-cyan-500",
  "from-purple-500 to-pink-500",
  "from-emerald-500 to-teal-500",
  "from-orange-500 to-red-500",
  "from-indigo-500 to-purple-500",
  "from-rose-500 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-cyan-500 to-blue-500",
  "from-violet-500 to-purple-500",
  "from-lime-500 to-green-500",
];

function EmployeePortrait({ employee, isEliminated, onClick }) {
  const initials = `${employee.prenom[0]}${employee.nom[0]}`;
  const gradient = COLORS[employee.id % COLORS.length];

  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all duration-300 text-center w-full
        ${isEliminated
          ? "border-slate-700 bg-slate-900/80 opacity-40 saturate-0"
          : "border-slate-600 bg-slate-800/60 hover:border-blue-500 hover:bg-slate-700/80"
        }
      `}
    >
      <div className={`
        w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0
        ${isEliminated ? "bg-slate-700" : `bg-gradient-to-br ${gradient}`}
      `}>
        {initials}
      </div>

      <div className={`font-semibold leading-tight w-full text-[11px] ${isEliminated ? "text-slate-600" : "text-slate-200"}`}>
        {employee.prenom}<br />{employee.nom}
      </div>

      <div className={`text-[10px] leading-tight w-full line-clamp-2 ${isEliminated ? "text-slate-700" : "text-slate-400"}`}>
        {employee.poste}
      </div>

      <div className={`text-[10px] font-mono font-bold ${isEliminated ? "text-slate-700" : "text-blue-400"}`}>
        #{employee.id}
      </div>

      {isEliminated && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-3xl">❌</div>
        </div>
      )}
    </button>
  );
}

export default function EmployeeElimination({ onSuccess, sessionCode, stage, playerId }) {
  const [eliminated, setEliminated] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  const [showSuccess, setShowSuccess] = useState(() => {
    try { return localStorage.getItem(SUCCESS_KEY) === "true"; } catch { return false; }
  });

  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(eliminated))); } catch { }
  }, [eliminated]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const toggleElimination = (id) => {
    const next = new Set(eliminated);
    next.has(id) ? next.delete(id) : next.add(id);
    setEliminated(next);
    setError("");
  };

  const handleVerify = () => {
    const remaining = EMPLOYEES_PUZZLE.filter(e => !eliminated.has(e.id));
    if (remaining.length !== 1) {
      setError(`Il doit rester exactement 1 employé non éliminé (actuellement : ${remaining.length})`);
      return;
    }
    setIsChecking(true);
    setError("");
    setTimeout(() => {
      if (remaining[0].id === CORRECT_ANSWER) {
        setShowSuccess(true);
        try {
          localStorage.setItem(SUCCESS_KEY, "true");
          localStorage.removeItem(STORAGE_KEY);
        } catch { }
        setTimeout(() => onSuccess?.(CORRECT_ANSWER), 2000);
      } else {
        // ── trackError ──
        trackError({
          sessionCode,
          stage,
          playerId,
          reason: "employee_elimination_wrong_answer",
          meta: { guessedId: remaining[0].id },
        });
        setError("Ce n'est pas la bonne personne. Réinitialisation…");
        setCooldown(10);
        setTimeout(() => { setEliminated(new Set()); setError(""); setIsChecking(false); }, 2000);
      }
    }, 1000);
  };

  const remainingCount = EMPLOYEES_PUZZLE.length - eliminated.size;

  if (showSuccess) {
    const winner = EMPLOYEES_PUZZLE.find(e => e.id === CORRECT_ANSWER);
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-6">
        <div className="text-6xl animate-bounce">✓</div>
        <div className="text-2xl font-bold text-emerald-300">Témoin identifié !</div>
        <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border-2 border-emerald-500/50 text-center space-y-4">
          <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-5xl font-bold text-white animate-pulse">
            {winner.prenom[0]}{winner.nom[0]}
          </div>
          <div className="text-2xl font-bold text-white">{winner.prenom} {winner.nom}</div>
          <div className="text-lg text-slate-300">{winner.poste}</div>
          <div className="text-3xl font-mono font-bold text-emerald-300">ID #{winner.id}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 px-2 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
        <div className="text-blue-300 font-semibold text-sm mb-1">🔍 Mission : Identifier le témoin</div>
        <div className="text-slate-300 text-xs leading-relaxed">
          ORIGAMI a généré le dossier MIRAGIO le{" "}
          <span className="font-bold text-blue-300">jeudi entre 00h00 et 01h00</span>{" "}
          (nuit de jeudi à vendredi).
          Éliminez tous les employés qui n'étaient pas présents au bureau à ce moment-là.
          <br />
          <span className="text-amber-300">→ Il ne doit rester qu'UNE seule personne.</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-400">
          Employés restants :{" "}
          <span className="text-white font-bold text-lg">{remainingCount}</span>
          {" "}/ {EMPLOYEES_PUZZLE.length}
        </div>
        <button
          onClick={handleVerify}
          disabled={remainingCount !== 1 || isChecking || cooldown > 0}
          className={`px-5 py-2 rounded-lg font-semibold text-sm transition-all ${
            remainingCount === 1 && !isChecking && cooldown === 0
              ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg"
              : "bg-slate-700 text-slate-500 cursor-not-allowed"
          }`}
        >
          {cooldown > 0 ? `Attendre ${cooldown}s` : isChecking ? "Vérification…" : "Vérifier la réponse"}
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}
      >
        {EMPLOYEES_PUZZLE.map(employee => (
          <EmployeePortrait
            key={employee.id}
            employee={employee}
            isEliminated={eliminated.has(employee.id)}
            onClick={() => !isChecking && toggleElimination(employee.id)}
          />
        ))}
      </div>
    </div>
  );
}